// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Typography } from '@mui/material';
import { type Variant } from '@mui/material/styles/createTypography';
import { classNames } from '@shared/ui-kit/utils';
import { type ComponentProps, type FC, type ReactNode } from 'react';

export interface ScreenHeaderProps
  extends Omit<ComponentProps<'header'>, 'title' | 'children'> {
  title: ReactNode;
  titleVariant?: Variant;
  subtitle?: ReactNode;
  subtitleVariant?: Variant;
  icon?: ReactNode;
  iconPlacement?: 'left' | 'top';
  titleProps?: Omit<ComponentProps<typeof Typography>, 'variant' | 'children'>;
  subtitleProps?: Omit<ComponentProps<typeof Typography>, 'variant' | 'children'>;
}

export const ScreenHeader: FC<ScreenHeaderProps> = ({
  title,
  subtitle,
  icon,
  className,
  titleVariant = 'h3',
  subtitleVariant = 'body1',
  iconPlacement = 'left',
  titleProps,
  subtitleProps,
  ...props
}) => (
  <header
    className={classNames(
      'group flex flex-col items-start justify-center gap-2',
      className,
    )}
    {...props}
  >
    {iconPlacement === 'top' && icon}
    <div className="flex items-center justify-center gap-x-2">
      {iconPlacement === 'left' && icon}
      <Typography variant={titleVariant} data-testid="title" {...titleProps}>
        {title}
      </Typography>
    </div>
    {subtitle && (
      <Typography
        variant={subtitleVariant}
        data-testid="subtitle"
        {...subtitleProps}
      >
        {subtitle}
      </Typography>
    )}
  </header>
);
